// Apps → App Factory for an outside client: its own runs, from the AI package
// it reviews once to a built app it tests and submits for publishing on the
// app's Publish tab. No publisher, accounts or prices here — the server words
// every step itself (clientRunDto in routes/admin/factory.ts on the API).
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/Button';
import { useToastStore } from '@/store/toast';
import {
  cancelFactoryRun, getClientFactoryPools, listClientFactoryRuns,
  type ClientFactoryRun, type ClientFactoryStep, type ClientFactoryStepKey, type FactoryStepStatus,
} from '@/api/factory';
import { AppWizardModal } from './apps-setup/AppWizardModal';
import { STEP_DOT, STEP_TEXT, STEP_WORD, fmtDuration, fmtUnix } from './apps-setup/factory-ui';

const STEP_LABEL: Record<ClientFactoryStepKey, string> = {
  meta: 'Store package', review: 'Your review', services: 'App services', domain: 'Domain',
  policy: 'Privacy policy', loader: 'Loading screen', build: 'Build', ready: 'Ready',
};

/** What the run is on now, in a few words. */
function runHeadline(run: ClientFactoryRun): { text: string; status: FactoryStepStatus } {
  if (run.status === 'cancelled') return { text: 'Cancelled', status: 'skipped' };
  if (run.status === 'done') return { text: 'Ready — test it and submit it', status: 'done' };
  const failed = run.steps.find((s) => s.status === 'failed');
  if (failed) return { text: `${STEP_LABEL[failed.key]} failed`, status: 'failed' };
  if (run.steps.some((s) => s.status === 'needs_you')) return { text: 'Waiting for your review', status: 'needs_you' };
  const running = run.steps.filter((s) => s.status === 'running').map((s) => STEP_LABEL[s.key]);
  if (running.length) return { text: running.join(', '), status: 'running' };
  const waiting = run.steps.find((s) => s.status === 'waiting');
  return { text: waiting ? STEP_LABEL[waiting.key] : '—', status: 'waiting' };
}

export function ClientFactory() {
  const qc = useQueryClient();
  const toast = useToastStore((s) => s.push);
  // The wizard: a new run, or the review of an existing one.
  const [wizard, setWizard] = useState<{ runId?: string; jobId?: string } | null>(null);

  const runsQ = useQuery({ queryKey: ['client-factory-runs'], queryFn: listClientFactoryRuns, refetchInterval: 10_000 });
  const poolsQ = useQuery({ queryKey: ['client-factory-pools'], queryFn: getClientFactoryPools, refetchInterval: 30_000 });
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['client-factory-runs'] });
    void qc.invalidateQueries({ queryKey: ['client-factory-pools'] });
  };
  const cancel = useMutation({
    mutationFn: (id: string) => cancelFactoryRun(id),
    onSuccess: () => { toast('Run cancelled'); refresh(); },
    onError: (e: unknown) => toast(e instanceof Error ? e.message : 'Could not cancel the run'),
  });

  const pools = poolsQ.data;
  const runs = runsQ.data ?? [];
  return (
    <div className="max-w-6xl mx-auto p-4 space-y-4">
      <div className="flex flex-col sm:flex-row gap-3 sm:items-start sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-content">App Factory</h1>
          <p className="text-content-muted text-sm mt-1 max-w-3xl">
            Pick a brand project and a template; AI writes the store package — name, icon, texts, screenshots. Check it once and approve it,
            and the run sets up the app services, the domain, the privacy policy and the loading screen, then builds the app. When it is ready,
            test the APK and submit the app for publishing on its Publish tab.
          </p>
        </div>
        <div className="flex flex-col items-start sm:items-end gap-1 shrink-0">
          <Button
            onClick={() => setWizard({})}
            disabled={!pools?.can_start}
            title={pools?.reason ?? undefined}
          >
            + New app
          </Button>
          {pools?.reason && <span className="text-xs text-warning max-w-xs sm:text-right">{pools.reason}</span>}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Allowance label="Apps this month" used={pools?.runs_used} limit={pools?.runs_limit} />
        <Allowance label="Own domains this month" used={pools?.domains_used} limit={pools?.domains_limit} />
        <Allowance label="In progress" used={pools?.running} limit={pools?.running_limit} />
      </div>
      {poolsQ.error && <p className="text-sm text-danger">{(poolsQ.error as Error).message}</p>}

      <div className="space-y-3">
        {runsQ.isLoading && <div className="p-6 text-center text-sm text-content-subtle">Loading…</div>}
        {runsQ.error && <div className="p-6 text-center text-sm text-danger">{(runsQ.error as Error).message}</div>}
        {!runsQ.isLoading && !runsQ.error && runs.length === 0 && (
          <div className="p-8 text-center text-sm text-content-subtle bg-surface border border-dashed border-border rounded-lg">
            No apps yet — press “New app”.
          </div>
        )}
        {runs.map((run) => (
          <RunCard
            key={run.id}
            run={run}
            onReview={() => setWizard({ runId: run.id, jobId: run.wizard_job_id })}
            cancelling={cancel.isPending && cancel.variables === run.id}
            onCancel={() => {
              if (confirm('Cancel this run? What it already made stays, and the app it used still counts toward this month.')) cancel.mutate(run.id);
            }}
          />
        ))}
      </div>

      <AppWizardModal
        isOpen={!!wizard}
        onClose={() => { setWizard(null); refresh(); }}
        onUseDraft={() => {}}
        isTechnical={false}
        ownerAdminId=""
        owners={[]}
        onOwnerChange={() => {}}
        onCreated={() => refresh()}
        onFinished={() => {}}
        factory={wizard ? {
          client: true,
          runId: wizard.runId,
          jobId: wizard.jobId,
          onRunStarted: () => refresh(),
          onApproved: () => {
            toast('Approved — the app is being built now');
            setWizard(null);
            refresh();
          },
        } : undefined}
      />
    </div>
  );
}

function Allowance({ label, used, limit }: { label: string; used: number | undefined; limit: number | undefined }) {
  const known = used != null && limit != null;
  const full = known && used >= limit;
  return (
    <div className="bg-surface border border-border rounded-lg p-3">
      <div className="text-[11px] uppercase tracking-wide text-content-subtle">{label}</div>
      <div className={`mt-1 text-lg font-semibold ${!known ? 'text-content-subtle' : full ? 'text-warning' : 'text-content'}`}>
        {known ? `${used} / ${limit}` : '…'}
      </div>
    </div>
  );
}

function RunCard({ run, onReview, onCancel, cancelling }: {
  run: ClientFactoryRun; onReview: () => void; onCancel: () => void; cancelling: boolean;
}) {
  const head = runHeadline(run);
  // Steps stay open while the run works; a finished one folds to its dots.
  const [open, setOpen] = useState(run.status === 'running');
  const review = run.steps.find((s) => s.key === 'review');
  const canReview = run.status === 'running' && !!run.wizard_job_id && !!review && review.status !== 'skipped';

  return (
    <div className="bg-surface border border-border rounded-lg">
      <div className="p-3 flex flex-wrap items-center gap-3">
        {run.icon_url
          ? <img src={run.icon_url} alt="" className="w-10 h-10 rounded-lg object-cover border border-border shrink-0" />
          : <div className="w-10 h-10 rounded-lg bg-surface-muted border border-border shrink-0" />}
        <div className="min-w-0 flex-1">
          <div className="font-medium text-content truncate">{run.name ?? 'Generating…'}</div>
          <div className="text-xs text-content-subtle truncate">
            {run.bundle_id ? <code>{run.bundle_id}</code> : `Started ${fmtUnix(run.created_at)}`}
            {run.domain && <> · {run.domain}</>}
            {run.contact_email && <> · support {run.contact_email}</>}
          </div>
        </div>
        <div className="min-w-0 sm:text-right">
          <div className={`text-sm ${STEP_TEXT[head.status]}`}>{head.text}</div>
          <div className="text-xs text-content-subtle">{fmtDuration(run.created_at, run.finished_at)}</div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {canReview && (
            <Button size="sm" variant={review!.status === 'needs_you' ? 'primary' : 'secondary'} onClick={onReview}>
              {review!.status === 'needs_you' ? 'Review' : 'Open the package'}
            </Button>
          )}
          {run.status === 'done' && run.app_id && (
            <Link
              to={`/apps-setup?tab=share&app=${run.app_id}`}
              className="inline-flex items-center px-3 py-1.5 text-sm rounded-md bg-primary text-white hover:opacity-90"
            >
              Publish tab
            </Link>
          )}
          {run.can_cancel && (
            <Button size="sm" variant="secondary" loading={cancelling} onClick={onCancel}>Cancel</Button>
          )}
        </div>
      </div>

      <div className="px-3 pb-3">
        <button type="button" onClick={() => setOpen((v) => !v)} className="flex items-center gap-1.5" title={open ? 'Hide the steps' : 'Show the steps'}>
          {run.steps.map((s) => (
            <span
              key={s.key}
              title={`${STEP_LABEL[s.key]}: ${STEP_WORD[s.status]}${s.detail ? ` — ${s.detail}` : ''}`}
              className={`w-3 h-3 rounded-full border-2 ${STEP_DOT[s.status]}`}
            />
          ))}
          <span className="ml-1 text-xs text-content-subtle hover:text-content">{open ? 'Hide steps' : 'Steps'}</span>
        </button>
        {open && (
          <ol className="mt-3 grid sm:grid-cols-2 lg:grid-cols-4 gap-2">
            {run.steps.map((s) => <StepItem key={s.key} step={s} />)}
          </ol>
        )}
      </div>
    </div>
  );
}

function StepItem({ step }: { step: ClientFactoryStep }) {
  return (
    <li className="flex items-start gap-2.5 p-2.5 rounded-lg border border-border">
      <span className={`mt-1 w-3 h-3 rounded-full border-2 shrink-0 ${STEP_DOT[step.status]}`} />
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-x-2">
          <span className="text-sm font-medium text-content">{STEP_LABEL[step.key]}</span>
          <span className={`text-xs ${STEP_TEXT[step.status]}`}>{STEP_WORD[step.status]}</span>
        </div>
        {step.detail && (
          <div className={`text-xs mt-0.5 break-words ${step.status === 'failed' ? 'text-danger' : 'text-content-subtle'}`}>{step.detail}</div>
        )}
      </div>
    </li>
  );
}
